"use client"

import { useState, useEffect } from "react"
import { X } from "lucide-react"
import { MedicationType, useDashboardBannerStore } from "../store/use-dashboard-banner-store"
import { useSupplementaryQuestionnaireMutation } from "~/features/public/api/mutations"
import { useAuth } from "~/providers/auth.provider"
import { useNavigate } from "@tanstack/react-router"

interface QuestionnaireQuestion {
  id: string
  question: string
  type: "multiple-choice" | "single-choice"
  options: string[]
}

const questionnaireData: Record<MedicationType, QuestionnaireQuestion[]> = {
  Compounded_Semaglutide: [
    {
      id: "why-semaglutide",
      question: "Why did you choose Compounded Semaglutide as your medication?",
      type: "multiple-choice",
      options: [
        "It's the most affordable option.",
        "I'm comfortable with daily injections.",
        "I want to start my weight loss journey slowly.",
        "I've read about its effectiveness for weight loss.",
      ],
    },
    {
      id: "daily-injections",
      question: "Do you have concerns about weekly injections?",
      type: "single-choice",
      options: ["Yes", "No", "I'm not sure yet"],
    },
    {
      id: "stomach-issues",
      question: "Have you experienced any stomach issues (like GERD or acid reflux) before?",
      type: "single-choice",
      options: ["Yes", "No", "I'm not sure"],
    },
    {
      id: "awareness",
      question: "Are you aware of how Compounded Semaglutide works and its potential side effects?",
      type: "single-choice",
      options: ["Yes, I feel well-informed", "Somewhat", "No, I'm not aware"],
    },
  ],
  Zepbound: [
    {
      id: "why-zebbound",
      question: "Why did you choose Compounded Tirzepatide?",
      type: "multiple-choice",
      options: [
        "It has the best clinical studies",
        "I don't mind paying more for better results",
        "I prefer weekly injections",
        "I like the long-term success stories",
      ],
    },
    {
      id: "weekly-injections",
      question: "Are you comfortable with using weekly injection pens?",
      type: "single-choice",
      options: ["Yes", "No", "I'm not sure"],
    },
    {
      id: "clinical-studies",
      question: "Do you prefer a medication with long-term clinical studies?",
      type: "single-choice",
      options: ["Yes, I like proven results", "No, I'm okay with newer treatments", "I'm not sure yet"],
    },
    {
      id: "awareness",
      question: "Are you aware of how Zepbound works and its potential side effects?",
      type: "single-choice",
      options: ["Yes, I feel well-informed", "Somewhat", "No, I'm not aware"],
    },
    {
      id: "insurance",
      question: "Do you have insurance that might cover Zepbound?",
      type: "single-choice",
      options: ["Yes", "No", "Not sure"],
    },
    {
      id: "care-doctor",
      question: "Do you have a primary care doctor or a specialist you can consult if you experience side effects or concerns?",
      type: "single-choice",
      options: ["Yes", "No", "I'd like help finding one"],
    },
  ],
  Compounded_Tirzepatide: [
    {
      id: "why-tirzepatide",
      question: "Why did you choose Compounded Tirzepatide?",
      type: "multiple-choice",
      options: [
        "I want a more balanced option",
        "I've heard it's very effective",
        "I want to see significant weight loss",
      ],
    },
    {
      id: "weekly-injections",
      question: "Do you have concerns about weekly injections?",
      type: "single-choice",
      options: ["Yes", "No", "I'm not sure"],
    },
    {
      id: "blood-sugar",
      question: "Do you prefer a medication that also helps with blood sugar control?",
      type: "single-choice",
      options: ["Yes, I'm diabetic", "No, I just want to lose weight", "Not sure / No preference"],
    },
    {
      id: "awareness",
      question: "Are you aware of how Compounded Tirzepatide works and its potential side effects?",
      type: "single-choice",
      options: ["Yes, I feel well-informed", "Somewhat", "No, I'm not aware"],
    },
    {
      id: "eating",
      question: "Are you comfortable with the possibility of more intense appetite reduction and faster weight loss with Compounded Tirzepatide?",
      type: "single-choice",
      options: ["Yes, that's great", "No, I'm concerned", "Somewhat"],
    },
  ],
}

export function SupplementaryQuestionnaireModal() {
  const { setSupplementaryModalOpen, setSupplementaryCompleted } =
    useDashboardBannerStore()
    const {user} = useAuth()
  const { mutate, isPending: isSubmitting } = useSupplementaryQuestionnaireMutation()

  const [answers, setAnswers] = useState<Record<string, string[]>>({})
  const [showSkeleton, setShowSkeleton] = useState(true)
  const navigate = useNavigate()
  const [submittedAttempt, setSubmittedAttempt] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false)
    }, 500) // Show skeleton for 500ms if no data

    return () => clearTimeout(timer)
  }, [])

  const medicationName = user?.medical_plan?.medication?.name
  const questions: QuestionnaireQuestion[] = medicationName
    ? questionnaireData[medicationName as MedicationType]
    : []

  const handleAnswerChange = (questionId: string, option: string, isMultiple: boolean) => {
    setAnswers((prev) => {
      if (isMultiple) {
        const currentAnswers = prev[questionId] || []
        const newAnswers = currentAnswers.includes(option)
          ? currentAnswers.filter((a) => a !== option)
          : [...currentAnswers, option]
        return { ...prev, [questionId]: newAnswers }
      } else {
        return { ...prev, [questionId]: [option] }
      }
    })
  }

  const handleSubmit = () => {
    setSubmittedAttempt(true);

    const allAnswered = questions.every(question => {
      return (answers[question.id] || []).length > 0;
    });

    if (!allAnswered) {
      // Prevent submission if not all questions are answered
      return;
    }

    mutate({
      question_answer: answers as Record<string, unknown>,
    }, {
      onSuccess: () => {
        window.location.href = "/dashboard"
      }
    })
    setSupplementaryCompleted(true)
    setSupplementaryModalOpen(false)
  }

  const handleClose = () => {
    navigate({to: "/dashboard"})
  }

  return (
    <div className="relative w-full h-screen">
      <div className="w-full relative">
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 z-10"
        >
          <X className="h-5 w-5" />
        </button>

        <div className=" overflow-y-auto hide-scrollbar lg:px-6 px-0 pt-6 pb-2">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-900">Complete Supplementary Questionnaire</h2>
            <p className="mt-2 text-sm text-gray-600">
              Please complete this health questionnaire before your doctor reviews your case.
            </p>
          </div>

          <div className="flex flex-col items-center justify-center w-full pb-24">
            {medicationName && !showSkeleton ? (
              <div className="space-y-8 lg:w-[60%] w-full">
                {questions.map((question, index) => (
                  <div key={question.id} className={`rounded-[12px] bg-white shadow-md border p-4 ${submittedAttempt && (answers[question.id] || []).length === 0 ? 'border-red-500' : ''}`}>
                    <h3 className="mb-4 text-base font-medium text-gray-900">
                      {index + 1}. {question.question}
                    </h3>
                    <div className="space-y-3">
                      {question.options.map((option) => (
                        <label key={option} className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type={question.type === "multiple-choice" ? "checkbox" : "radio"}
                            name={question.id}
                            checked={answers[question.id]?.includes(option) || false}
                            onChange={() => handleAnswerChange(question.id, option, question.type === "multiple-choice")}
                            className="h-4 w-4 text-[#4272DD] focus:ring-[#4272DD] border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-8 w-[60%] animate-pulse">
                <div className="h-40 bg-gray-200 rounded-[12px] w-full"></div>
                <div className="h-40 bg-gray-200 rounded-[12px] w-full mt-8"></div>
                <div className="h-40 bg-gray-200 rounded-[12px] w-full mt-8"></div>
                <div className="h-40 bg-gray-200 rounded-[12px] w-full mt-8"></div>
                <div className="space-y-2"></div>
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-white border-t p-4 flex w-full justify-center z-10">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-[60%] rounded-lg bg-[#4272DD] px-6 py-3 text-base font-medium text-white transition-colors hover:bg-[#3461CC] focus:outline-none focus:ring-2 focus:ring-[#4272DD] focus:ring-offset-2"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  )
}
